<script lang="ts">
  /**
   * The admin page (R4.9 §7, Task 11): who this browser is signed in as, the
   * way out, and the admin's two capabilities — mint an invite code, and read
   * the list of people.
   *
   * A view state like settings (the keyboard layer is dead here, and the panel
   * authors nothing locally). It renders the account the gate RESOLVED, passed
   * in whole, and reads the admin half straight off the three admin routes with
   * a same-origin `fetch` — the login cookie rides along, and the relay answers
   * `403` to anyone who is not an admin. That refusal is why the admin calls are
   * gated on `is_admin` rather than attempted and handled: a panel that asked
   * anyway would be asking a question whose answer it must not act on.
   *
   * `[ Sign out ]` is the host's (the container owns the sign-out sequence, the
   * local-store cleanup included); this view only asks for it.
   *
   * A minted code renders plainly (§6): it is a secret the admin hands to one
   * person, so each open code carries a copy button that puts the CODE itself
   * on the clipboard — there is no share surface, and no way to revoke one
   * from here (that is not one of the two capabilities). Beside each code is
   * the full sign-in URL the code rides, the thing the admin actually sends.
   */
  import CircleUser from 'lucide/dist/esm/icons/circle-user.mjs';
  import Copy from 'lucide/dist/esm/icons/copy.mjs';
  import LogOut from 'lucide/dist/esm/icons/log-out.mjs';
  import TicketPlus from 'lucide/dist/esm/icons/ticket-plus.mjs';
  import Trash from 'lucide/dist/esm/icons/trash.mjs';
  import X from 'lucide/dist/esm/icons/x.mjs';
  import type { Account } from '../storage/account';
  import Icon from './Icon.svelte';

  /**
   * One row of `GET /api/admin/users`.
   *
   * Deliberately NOT an `Account`. The relay serves the account id as `id`
   * (the same serialisation `/api/auth/me` serves, `db::accounts::Account`),
   * while `Account` spells it `owner_id`, and the two must never be confused by
   * a silent read of one as the other: this type keeps the wire's own name, and
   * nothing here turns a row into an identity. The panel shows what the relay
   * sent — email and display name — and the pinned shape carries no join date
   * for the list §7 describes. `is_admin` is typed because the row is the
   * contract, and rendered nowhere: there are no roles to manage here, and this
   * panel changes nothing about who is an admin.
   */
  interface AdminUser {
    id: string;
    email: string;
    name: string | null;
    is_admin: boolean;
  }

  interface Props {
    /** The signed-in account, as the gate resolved it. */
    account: Account;
    /** Ends the login. The host owns everything that follows (store cleanup). */
    onSignOut: () => void;
    /** Leaves the panel — back to settings, which is where it is reached from. */
    onBack: () => void;
    /**
     * Opens the account-deletion confirm modal (R5.5). The modal and the
     * DELETE belong to the host, which owns the sign-out that a successful
     * deletion must follow with; absent on standalone mounts, where the
     * affordance simply does not render.
     */
    onDeleteAccount?: () => void;
  }

  let { account, onSignOut, onBack, onDeleteAccount }: Props = $props();

  /**
   * The URL to send the person an invite is for: the sign-in flow the code
   * rides (§6), at this browser's own origin. Built here, at render, because
   * the deployment's address is wherever the admin is looking at this panel.
   */
  const inviteUrl = (code: string): string =>
    `${window.location.origin}/api/auth/signin?invite=${encodeURIComponent(code)}`;

  /**
   * The admin half's data, empty until the relay answers (admins only).
   *
   * Always ARRAYS, whatever the relay sent. Both lists are rendered by a KEYED
   * `{#each}`, and `invites.length` is read below: a malformed `200` — a proxy's
   * error page, a future relay that renames the field — would otherwise throw on
   * the length or hand the keyed list duplicate keys, which Svelte treats as a
   * render error. A blank list is the honest degradation; a refusal has its own
   * notice (`adminError`).
   */
  let users = $state<AdminUser[]>([]);
  let invites = $state<string[]>([]);
  /** A refused or unreachable admin route, said out loud rather than shown as an empty list. */
  let adminError = $state<string | null>(null);
  /** One mint in flight; the button stays reachable, not dead (DebugLogView's pattern). */
  let minting = $state(false);

  /**
   * The open code whose copy the panel just performed, said back as "Copied"
   * on its own row — the feedback the click leaves behind, perceivable without
   * watching the button (`aria-live`). One code at a time: the last copy wins.
   */
  let copiedCode = $state<string | null>(null);

  /**
   * Copies the CODE itself — the secret the admin hands to one person — to the
   * clipboard. A failure is said out loud in the same notice a refused admin
   * route uses, rather than the row claiming a copy that did not land.
   */
  async function copyInvite(code: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      copiedCode = code;
    } catch (e) {
      adminError = e instanceof Error ? e.message : String(e);
    }
  }

  /**
   * The traffic `load` turns into the two lists.
   *
   * Two things are not trusted. Which field the body has: a `200` whose shape is
   * not ours is not a list. And the keys: a keyed `{#each}` throws on a duplicate
   * key, and the relay's own contract — one row per id, one unused code per value
   * — is not something a render should depend on.
   */
  function listFrom<T>(
    body: unknown,
    field: string,
    usable: (row: unknown) => row is T,
    keyOf: (row: T) => string,
  ): T[] {
    const value = (body as Record<string, unknown> | null | undefined)?.[field];
    if (!Array.isArray(value)) return [];
    const seen = new Set<string>();
    const rows: T[] = [];
    for (const row of value) {
      if (!usable(row)) continue;
      const key = keyOf(row);
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(row);
    }
    return rows;
  }

  /** A user row, as far as this panel renders one. */
  const isAdminUser = (row: unknown): row is AdminUser =>
    typeof row === 'object' && row !== null && typeof (row as AdminUser).id === 'string';

  const isInviteCode = (row: unknown): row is string => typeof row === 'string';

  async function load(): Promise<void> {
    adminError = null;
    try {
      const [people, codes] = await Promise.all([
        fetch('/api/admin/users'),
        fetch('/api/admin/invites'),
      ]);
      if (!people.ok) throw new Error(`users: ${people.status}`);
      if (!codes.ok) throw new Error(`invites: ${codes.status}`);
      users = listFrom<AdminUser>(await people.json(), 'users', isAdminUser, (user) => user.id);
      invites = listFrom<string>(await codes.json(), 'invites', isInviteCode, (code) => code);
    } catch (e) {
      adminError = e instanceof Error ? e.message : String(e);
    }
  }

  $effect(() => {
    if (account.is_admin) void load();
  });

  /**
   * Mints one code: a `POST` the relay answers `201 {"code"}` to. The code is
   * PREPENDED to the open list the panel is already showing, so what was just
   * minted is on screen without a second round trip. The filter keeps the keyed
   * list's keys unique — the same code twice would be two rows under one key,
   * which is a render error rather than a cosmetic duplicate.
   */
  async function mintInvite(): Promise<void> {
    if (minting) return;
    minting = true;
    adminError = null;
    try {
      const response = await fetch('/api/admin/invites', { method: 'POST' });
      if (!response.ok) throw new Error(`invites: ${response.status}`);
      const { code } = (await response.json()) as { code: string };
      invites = [code, ...invites.filter((open) => open !== code)];
    } catch (e) {
      adminError = e instanceof Error ? e.message : String(e);
    } finally {
      minting = false;
    }
  }
</script>

<div class="admin-view" data-testid="admin-view">
  <header class="admin-header">
    <h1 tabindex="-1">Admin</h1>
    <button class="action" type="button" data-testid="admin-back" onclick={onBack}>
      [ Back to settings ]
    </button>
  </header>

  <p class="admin-identity">
    <Icon node={CircleUser} size={14} />
    <span data-testid="admin-email">{account.email}</span>
    {#if account.name}
      <span class="admin-name">{account.name}</span>
    {/if}
  </p>

  <button class="action" type="button" data-testid="admin-signout" onclick={onSignOut}>
    <Icon node={LogOut} size={14} />
    <span>[ Sign out ]</span>
  </button>

  {#if account.is_admin}
    <section class="admin-admin" data-testid="admin-admin">
      <h2>Admin</h2>

      <button
        class="action"
        type="button"
        data-testid="admin-mint-invite"
        aria-disabled={minting}
        onclick={() => void mintInvite()}
      >
        <Icon node={TicketPlus} size={14} />
        <span>[ Mint invite code ]</span>
      </button>

      {#if onDeleteAccount}
        <button
          class="action"
          type="button"
          data-testid="admin-delete-account"
          onclick={onDeleteAccount}
        >
          <Icon node={Trash} size={14} />
          <span>[ Delete account ]</span>
        </button>
      {/if}

      <h3>People</h3>
      <ul class="admin-rows" data-testid="admin-users">
        {#each users as user (user.id)}
          <li class="admin-row" data-testid="admin-user-row">
            <span data-testid="admin-user-email">{user.email}</span>
            {#if user.name}
              <span class="admin-row-note">{user.name}</span>
            {/if}
          </li>
        {/each}
      </ul>

      <h3>Open invite codes</h3>
      <ul class="admin-rows" data-testid="admin-invites">
        {#each invites as code (code)}
          <li class="admin-row">
            <code data-testid="admin-invite-code">{code}</code>
            <!-- The copy: the CODE is the secret the admin hands over, and the
                 panel carries that copy itself. -->
            <button
              class="admin-invite-copy"
              type="button"
              data-testid="admin-invite-copy"
              aria-label={`Copy invite code ${code}`}
              onclick={() => void copyInvite(code)}
            >
              <Icon node={Copy} size={14} />
            </button>
            {#if copiedCode === code}
              <span class="admin-row-note" data-testid="admin-invite-copied" aria-live="polite">
                Copied
              </span>
            {/if}
            <!-- The handoff: the full link to send the person being invited,
                 the sign-in flow the code rides (§6), at this deployment's
                 own origin. Plain text, like the code — no share surface. -->
            <code class="admin-invite-url" data-testid="admin-invite-url">
              {inviteUrl(code)}
            </code>
          </li>
        {/each}
      </ul>
      {#if invites.length === 0}
        <p class="admin-empty">No open codes.</p>
      {/if}
    </section>
  {/if}
</div>

<!--
  A refused or unreachable admin route — the same failure notice shape the
  settings page uses for a failed import (paper.css' `.toast`), because an empty
  list and a 403 look identical otherwise, and one of them is a lie. One state,
  one site; Dismiss clears it in place.
-->
{#if adminError !== null}
  <div class="toast rejection-notice" data-testid="admin-admin-error" aria-live="polite">
    <span>{adminError}</span>
    <button class="action" type="button" onclick={() => (adminError = null)}>
      <Icon node={X} />Dismiss
    </button>
  </div>
{/if}

<style>
  .admin-view {
    width: min(100%, 60em);
    margin: 0 auto;
    padding: 2rem 1rem;
  }

  .admin-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  h1 {
    font-family: inherit;
    font-size: 1.4rem;
    font-weight: normal;
  }

  h2 {
    font-family: inherit;
    font-size: 1.1rem;
    font-weight: normal;
  }

  h3 {
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: normal;
    color: var(--ink-muted);
    margin: 1.25rem 0 0.25rem;
  }

  .admin-identity {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin: 1.25rem 0 0.75rem;
  }

  .admin-name {
    color: var(--ink-muted);
  }

  .admin-admin {
    margin-top: 2rem;
    border-top: 1px solid color-mix(in srgb, var(--ink-muted) 30%, transparent);
  }

  .admin-rows {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .admin-row {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    padding: 0.35rem 0;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 20%, transparent);
  }

  .admin-row-note,
  .admin-empty {
    color: var(--ink-muted);
  }

  .admin-row code {
    font-family: var(--font-mono, monospace);
    font-size: 12px;
  }

  /* The URL is the long half of the row, so it gets what is left over and
     wraps rather than pushing the row wider. */
  .admin-invite-url {
    flex: 1;
    min-width: 0;
    color: var(--ink-muted);
    overflow-wrap: anywhere;
  }

  .admin-invite-copy {
    display: inline-flex;
    align-items: center;
    flex: none;
    padding: 0.15rem;
    border: 1px solid color-mix(in srgb, var(--ink-muted) 30%, transparent);
    border-radius: 3px;
    background: none;
    color: var(--ink-muted);
    cursor: pointer;
  }
</style>
